/**
 * `<PanelPicker>` — unified color + icon + delete picker for every
 * panel kind (info / note / success / warning / error / customPanel).
 * Renamed/generalized from `CustomPanelPicker` on 2026-05-29 so the
 * named panels can carry the same overridable visual identity as the
 * explicit customPanel.
 *
 * Architecture:
 *   - Floating popover anchored to the panel's bottom edge via CSS
 *     `absolute top-full -mt-3 -translate-x-1/2`. No tippy/JS reflow,
 *     no jitter on keystroke as the panel grows.
 *   - Visible only when this specific panel contains the editor
 *     selection. Read-only docs don't render the picker at all.
 *   - Writes `color` + `icon` attrs back to the panel's node via
 *     `updateAttributes(nodeName, ...)`. The NodeView reads the
 *     resulting attrs and falls back to the kind's defaults when
 *     missing.
 *
 * Layout:
 *   [● ● ● ● ● ● ● ● ● ● ● ● ●]   |   [⬇ icon]   |   [⋮ kebab]
 */

import type { Editor } from '@tiptap/react'
import { ChevronDown, MoreVertical, Trash2 } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  CUSTOM_PANEL_COLORS,
  CUSTOM_PANEL_COLOR_SWATCH_HEX,
  CUSTOM_PANEL_ICONS,
  type CustomPanelColor,
  type CustomPanelIcon,
  type PanelNodeName,
} from './panel-kinds'

export interface PanelPickerProps {
  editor: Editor
  /** Resolver for this NodeView's start position in the doc. */
  getPos: (() => number | undefined) | null
  /** ProseMirror node name — `infoPanel`, `customPanel`, etc. */
  nodeName: PanelNodeName
  /** The panel's `nodeSize` (start pos → end pos delta). */
  nodeSize: number
  /** Currently-resolved color slug (attr or kind default). */
  currentColor: CustomPanelColor
  /** Currently-resolved icon slug (attr or kind default). */
  currentIcon: CustomPanelIcon
}

export function PanelPicker({
  editor,
  getPos,
  nodeName,
  nodeSize,
  currentColor,
  currentIcon,
}: PanelPickerProps) {
  const { t } = useTranslation('wiki')

  // Track whether THIS particular panel is the active one. The
  // `isThisPanelActive` walk scopes to this node's pos range so a
  // doc with several panels doesn't render every picker at once.
  const [isActive, setIsActive] = useState(() => isThisPanelActive(editor, getPos, nodeSize))

  useEffect(() => {
    const update = () => setIsActive(isThisPanelActive(editor, getPos, nodeSize))
    editor.on('selectionUpdate', update)
    editor.on('transaction', update)
    return () => {
      editor.off('selectionUpdate', update)
      editor.off('transaction', update)
    }
  }, [editor, getPos, nodeSize])

  const updateColor = useCallback(
    (color: CustomPanelColor) => {
      editor.chain().focus().updateAttributes(nodeName, { color }).run()
    },
    [editor, nodeName],
  )

  const updateIcon = useCallback(
    (icon: CustomPanelIcon) => {
      editor.chain().focus().updateAttributes(nodeName, { icon }).run()
    },
    [editor, nodeName],
  )

  const deletePanel = useCallback(() => {
    if (!getPos) return
    const pos = getPos()
    if (typeof pos !== 'number') return
    // Replace the panel node's full extent with nothing → removes it.
    // The cursor lands at the deletion point automatically.
    editor
      .chain()
      .focus()
      .deleteRange({ from: pos, to: pos + nodeSize })
      .run()
  }, [editor, getPos, nodeSize])

  if (!isActive || editor.isEditable === false) return null

  return (
    <div
      contentEditable={false}
      // Floating popover anchored via CSS to the wrapper's bottom-center.
      // `-mt-3` overlaps the panel's bottom padding zone slightly.
      className="absolute start-1/2 top-full z-10 -mt-3 flex -translate-x-1/2 items-center gap-2 whitespace-nowrap rounded-md border border-border bg-bg-elevated px-3 py-2 shadow-md"
      onMouseDown={(e) => e.stopPropagation()}
    >
      {/* Color swatches */}
      <div
        role="radiogroup"
        aria-label={t('panel.custom.colorLabel', { defaultValue: 'Color' })}
        className="flex items-center gap-1.5"
      >
        {CUSTOM_PANEL_COLORS.map((color) => {
          const isSelected = color === currentColor
          return (
            <button
              key={color}
              type="button"
              role="radio"
              aria-checked={isSelected}
              aria-label={t('panel.custom.colorSwatchAria', {
                color,
                defaultValue: `${color} color`,
              })}
              onMouseDown={(e) => {
                e.preventDefault()
                updateColor(color)
              }}
              className={`h-[18px] w-[18px] rounded-full transition focus-visible:outline-none ${
                isSelected
                  ? 'ring-2 ring-accent ring-offset-2 ring-offset-bg-elevated'
                  : 'ring-1 ring-border hover:ring-border-strong'
              }`}
              style={{ backgroundColor: CUSTOM_PANEL_COLOR_SWATCH_HEX[color] }}
              title={color}
            />
          )
        })}
      </div>

      <span aria-hidden="true" className="h-5 w-px bg-border" />

      {/* Icon dropdown */}
      <IconPickerButton value={currentIcon} onChange={updateIcon} t={t} />

      <span aria-hidden="true" className="h-5 w-px bg-border" />

      {/* Kebab menu — Delete is the only action today (2026-05-29). */}
      <KebabMenu onDelete={deletePanel} t={t} />
    </div>
  )
}

/**
 * `isThisPanelActive(editor, getPos, nodeSize)` — true when the editor
 * selection is contained within `[panelPos, panelPos + nodeSize)`.
 */
function isThisPanelActive(
  editor: Editor,
  getPos: (() => number | undefined) | null,
  nodeSize: number,
): boolean {
  if (!getPos) return false
  const pos = getPos()
  if (typeof pos !== 'number') return false
  const { from, to } = editor.state.selection
  return from >= pos && to <= pos + nodeSize
}

interface IconPickerButtonProps {
  value: CustomPanelIcon
  onChange: (icon: CustomPanelIcon) => void
  t: ReturnType<typeof useTranslation<'wiki'>>['t']
}

function IconPickerButton({ value, onChange, t }: IconPickerButtonProps) {
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement | null>(null)
  const CurrentIcon = CUSTOM_PANEL_ICONS[value]

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (!rootRef.current) return
      if (rootRef.current.contains(e.target as Node)) return
      setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        aria-haspopup="grid"
        aria-expanded={open}
        aria-label={t('panel.custom.iconLabel', { defaultValue: 'Icon' })}
        onMouseDown={(e) => {
          e.preventDefault()
          setOpen((o) => !o)
        }}
        className="flex items-center gap-1 rounded border border-border bg-bg px-1.5 py-1 text-fg hover:border-border-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        title={t('panel.custom.iconLabel', { defaultValue: 'Icon' })}
      >
        <CurrentIcon size={16} aria-hidden="true" />
        <ChevronDown size={12} aria-hidden="true" className="text-fg-subtle" />
      </button>

      {open ? (
        <div
          // biome-ignore lint/a11y/useSemanticElements: native <table> doesn't fit a 4×3 icon grid layout; role="grid" + role="gridcell" is the supported ARIA pattern for non-tabular grid pickers.
          role="grid"
          aria-label={t('panel.custom.iconGridAria', { defaultValue: 'Icon picker' })}
          className="absolute bottom-full z-50 mb-1 start-0 grid grid-cols-4 gap-1 rounded-md border border-border-strong bg-bg-elevated p-1 shadow-lg"
          style={{ minWidth: 144 }}
        >
          {(Object.keys(CUSTOM_PANEL_ICONS) as CustomPanelIcon[]).map((slug) => {
            const Icon = CUSTOM_PANEL_ICONS[slug]
            const isSelected = slug === value
            return (
              <button
                key={slug}
                type="button"
                // biome-ignore lint/a11y/useSemanticElements: button is already clickable; role="gridcell" surfaces its place in the grid for AT.
                role="gridcell"
                aria-label={slug}
                aria-selected={isSelected}
                onMouseDown={(e) => {
                  e.preventDefault()
                  onChange(slug)
                  setOpen(false)
                }}
                className={`flex h-7 w-7 items-center justify-center rounded ${
                  isSelected
                    ? 'bg-bg-muted text-fg'
                    : 'text-fg-muted hover:bg-bg-muted hover:text-fg'
                } focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent`}
              >
                <Icon size={16} aria-hidden="true" />
              </button>
            )
          })}
        </div>
      ) : null}
    </div>
  )
}

interface KebabMenuProps {
  onDelete: () => void
  t: ReturnType<typeof useTranslation<'wiki'>>['t']
}

function KebabMenu({ onDelete, t }: KebabMenuProps) {
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (!rootRef.current) return
      if (rootRef.current.contains(e.target as Node)) return
      setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const menuLabel = t('panel.custom.menuLabel', { defaultValue: 'Panel menu' })
  const deleteLabel = t('panel.custom.deleteLabel', { defaultValue: 'Delete panel' })

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={menuLabel}
        onMouseDown={(e) => {
          e.preventDefault()
          setOpen((o) => !o)
        }}
        className="flex h-7 w-7 items-center justify-center rounded text-fg-muted hover:bg-bg-muted hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        title={menuLabel}
      >
        <MoreVertical size={16} aria-hidden="true" />
      </button>

      {open ? (
        <div
          role="menu"
          aria-label={menuLabel}
          // Opens UPWARD from the picker bar so the menu stays inside
          // the viewport when the panel sits near the page bottom.
          className="absolute bottom-full z-50 mb-1 end-0 min-w-[160px] rounded-md border border-border-strong bg-bg-elevated p-1 shadow-lg"
        >
          <button
            type="button"
            role="menuitem"
            onMouseDown={(e) => {
              e.preventDefault()
              setOpen(false)
              onDelete()
            }}
            className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-start text-sm text-fg hover:bg-bg-muted focus-visible:outline-none focus-visible:bg-bg-muted"
          >
            <Trash2 size={14} aria-hidden="true" className="flex-none text-fg-muted" />
            <span>{deleteLabel}</span>
          </button>
        </div>
      ) : null}
    </div>
  )
}

export { isThisPanelActive as __test_isThisPanelActive }
